import { t } from '@/i18n/i18ns'
import _ from 'lodash'
import TableColumnInput from './table-column-input/table-column-input-tmpl'
import TableColumnText from './table-column-text/table-column-text-tmpl'
import TableColumnButton from './table-column-button/table-column-button-tmpl'
import TableColumnExpandable from './table-column-expandable/table-column-expandable-tmpl'
import TableColumnIcon from './table-column-icon/table-column-icon-tmpl'
import TableColumnLabelWithIcon from './table-column-label-with-icon/table-column-label-with-icon-tmpl'
import TableColumnSpinner from './table-column-spinner/table-column-spinner-tmpl'

const ACTION_TYPES = {
  submitted: 'licenseOrdering.cancelOrder',
  suspend: 'activation.suspend',
  restore: 'activation.restore',
  download: 'activation.download'
}

export default {
  name: 'ActivateTable',
  components: {
    TableColumnInput,
    TableColumnText,
    TableColumnExpandable,
    TableColumnIcon,
    TableColumnButton,
    TableColumnLabelWithIcon,
    TableColumnSpinner
  },
  props: {
    tableColumns: {
      type: Array,
      default: () => []
    },
    tableData: {
      type: Array,
      default: []
    },
    isTableEmpty: {
      type: Boolean
    },
    searchResult: {
      type: Object,
      default: () => ({ isEmpty: false })
    },
    noEntriesText: {
      type: String
    }
  },
  data () {
    return {
      internalTableColumns: this.tableColumns,
      internalTableData: this.tableData,
      activeSortColumn: {
        order: null,
        columnKey: null
      },
      sortOrder: {
        asc: 1,
        desc: -1
      }
    }
  },
  watch: {
    tableColumns: {
      immediate: true,
      deep: true,
      handler (newVal) {
        this.internalTableColumns = newVal
      }
    },
    tableData: {
      immediate: true,
      deep: true,
      handler (newVal) {
        this.internalTableData = newVal
      }
    }
  },
  computed: {
    columnComponents () {
      return {
        input: TableColumnInput,
        text: TableColumnText,
        expandable: TableColumnExpandable,
        expand: TableColumnIcon,
        action: TableColumnButton,
        license: TableColumnLabelWithIcon,
        spinner: TableColumnSpinner
      }
    },
    columnComponentProps () {
      const props = {}

      this.internalTableData.forEach((row, index) => {
        props[row.id] = {}
        this.internalTableColumns.forEach((column) => {
          switch (column.type) {
            case 'text':
              props[row.id][column.key] = {
                text: row[column.key],
                stylingClasses: column?.tdValueStylingClasses ?? []
              }
              break
            case 'input': {
              const cellValue = row[column.key]
              const columnKeyProps = {
                index,
                columnKey: column.key,
                id: row.id
              }

              /**
               * In certain cases, the cell value is an object due to customized styling and attributes of the element, such as disabling the input and hiding the bottom border.
               */
              if (_.isPlainObject(cellValue)) {
                columnKeyProps.value = cellValue?.value ?? ''
                columnKeyProps.placeholder = cellValue?.placeholder ?? column.placeholder
                columnKeyProps.stylingClasses = cellValue?.tdValueStylingClasses ?? []
                columnKeyProps.elAttribute = cellValue?.elAttribute ?? {}
              } else {
                columnKeyProps.value = cellValue
                columnKeyProps.placeholder = column.placeholder
                columnKeyProps.stylingClasses = column?.tdValueStylingClasses ?? []
              }

              props[row.id] = {
                ...props[row.id],
                [column.key]: columnKeyProps
              }
              break
            }
            case 'license':
              props[row.id][column.key] = {
                license: row[column.key],
                index,
                stylingClasses: column?.tdValueStylingClasses ?? []
              }
              break
            case 'expandable':
              props[row.id] = {
                ...props[row.id],
                [column.key]: {
                  value: row[column.key]?.count,
                  extraItems: row[column.key]?.licenseCode,
                  expanded: row.expanded,
                  stylingClasses: column?.tdValueStylingClasses ?? []
                }
              }
              break
            case 'expand':
              props[row.id][column.key] = {
                index,
                expanded: row[column.key],
                stylingClasses: column?.tdValueStylingClasses ?? []
              }
              break
            case 'action': {
              const cellValue = row[column.key]
              const columnKeyProps = {
                index
              }

              /**
               * In certain cases, the cell value is an object, particularly when the action column features a custom component like a spinner instead of a button
               */
              if (_.isPlainObject(cellValue)) {
                columnKeyProps.text = cellValue.cellComponent ? cellValue.cellComponent.text : ''
                columnKeyProps.stylingClasses = cellValue.cellComponent ? cellValue.cellComponent.stylingClasses : ''
              } else {
                columnKeyProps.text = ACTION_TYPES[row[column.key]] ? t(ACTION_TYPES[row[column.key]]) : ''
                columnKeyProps.stylingClasses = row[column.key] === 'restore' ? 'active' : ''
              }

              props[row.id] = {
                ...props[row.id],
                [column.key]: columnKeyProps
              }
              break
            }
            default:
              props[row.id][column.key] = {
                value: row
              }
          }
        })
      })

      return props
    }
  },
  methods: {
    handleEvent (eventType, data) {
      this.$emit(eventType, data)
    },
    handleExpandRow (index) {
      this.$emit('expand', index)
    },
    handleButtonClick (index) {
      this.$emit('buttonClick', index)
    },
    handleColumnEvent (columnType, eventType, eventData) {
      switch (columnType) {
        case 'expand':
          this.handleExpandRow(eventData)
          break
        case 'action':
          this.handleButtonClick(eventData)
          break
        case 'input':
          if (eventType === 'change') {
            this.handleEvent('inputChange', eventData)
          } else if (eventType === 'focus') {
            this.handleEvent('inputFocus', eventData)
          } else if (eventType === 'blur') {
            this.handleEvent('inputBlur', eventData)
          } else if (eventType === 'enter') {
            this.handleEvent('inputEnter', eventData)
          }
          break
      }
    },
    /**
     * This function determines which component should be retrieved and loaded into the template.
     * In certain cases, columns may have a dynamic component type, such as the action column, which may include a spinner component.
     */
    getComponent (column, row) {
      let component = null

      if (_.isEmpty(column)) {
        return component
      }

      const cellValue = row[column.key]
      if (_.isPlainObject(cellValue) && !_.isEmpty(cellValue.cellComponent) && !_.isEmpty(cellValue.cellComponent.type)) {
        component = this.columnComponents[cellValue.cellComponent.type]
      } else {
        component = this.columnComponents[column.type]
      }

      return component
    },
    handleColumnSort (columnKey) {
      if (columnKey === this.activeSortColumn.columnKey) {
        // Sort sequence. 1st click: SORT ASC, 2nd click: SORT DESC, 3rd click: UNSORT
        switch (this.activeSortColumn.order) {
          case this.sortOrder.desc:
            this.activeSortColumn.order = null
            break
          case this.sortOrder.asc:
            this.activeSortColumn.order = this.sortOrder.desc
            break
          default:
            this.activeSortColumn.order = this.sortOrder.asc
        }
      } else {
        // Reset sort order if column target changed
        this.activeSortColumn.order = this.sortOrder.asc
      }

      this.activeSortColumn.columnKey = columnKey

      this.$emit('sort', {
        order: this.activeSortColumn.order,
        columnKey
      })
    },
    isColumnSorted (column, order) {
      return this.activeSortColumn.columnKey === column.key && this.activeSortColumn.order === order
    }
  },
  mounted () {
    this.internalTableColumns = this.tableColumns
    this.internalTableData = this.tableData
  }
}
